import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProducts } from '../api';
import { Carousel, CarouselContent, CarouselItem } from './ui/carousel';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Flame } from 'lucide-react';

export default function HotDealsSection() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProducts({ hotDeal: true, limit: 10 })
      .then(data => setProducts(data.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  if (products.length === 0 && !loading) return null;

  return (
    <section className="w-full py-8 md:py-12 bg-muted/30">
      <div className="container px-4">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Flame className="h-6 w-6 text-brand-red" />
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">HOT DEALS</h2>
          </div>
          <Link
            to="/shop?hotDeal=true"
            className="text-sm font-medium text-foreground hover:underline"
          >
            View All
          </Link>
        </div>

        {/* Carousel */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <CardContent className="p-4">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-5 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Carousel
            opts={{
              align: 'start',
              slidesToScroll: 2,
              loop: true,
              duration: 400,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {products.map((product) => (
                <CarouselItem key={product._id} className="pl-4 md:basis-1/2 lg:basis-1/4">
                  <ProductCard product={product} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        )}
      </div>
    </section>
  );
}

function ProductCard({ product }) {
  const formatPrice = (price) =>
    price?.toLocaleString('en-CA', { minimumFractionDigits: 2, style: 'currency', currency: 'CAD' });

  const conditionColors = {
    'New': 'bg-foreground text-background',
    'NOS': 'bg-brand-yellow text-foreground',
    'Used – Normal Use': 'bg-muted text-foreground',
    'Used – Fair': 'bg-brand-red text-background',
  };

  return (
    <Link to={`/product/${product._id}`} className="block">
      <Card className="overflow-hidden border-border hover:shadow-lg transition-shadow duration-200 h-full">
        {/* Image */}
        <div className="relative bg-muted/50 aspect-square overflow-hidden">
          {product.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              No Image
            </div>
          )}
          <Badge
            className={`absolute top-2 left-2 ${conditionColors[product.condition] || 'bg-muted'}`}
          >
            {product.condition}
          </Badge>
          {/* Hot Deal Badge */}
          <Badge className="absolute top-2 right-2 bg-brand-red text-background">
            HOT
          </Badge>
        </div>

        {/* Content */}
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            {product.partCategory}
          </p>
          <h3 className="text-sm font-semibold text-foreground line-clamp-2 mb-2 leading-tight">
            {product.title}
          </h3>
          {product.fitment?.[0] && (
            <p className="text-xs text-muted-foreground mb-2">
              {product.fitment[0].yearFrom}–{product.fitment[0].yearTo} {product.fitment[0].make}
            </p>
          )}
          <div className="flex items-center justify-between">
            <p className="text-lg font-bold text-foreground">
              {formatPrice(product.price)}
            </p>
            {product.compareAtPrice && (
              <p className="text-xs text-muted-foreground line-through">
                {formatPrice(product.compareAtPrice)}
              </p>
            )}
          </div>
          <Button
            size="sm"
            className="w-full mt-3 bg-foreground text-background hover:bg-muted"
          >
            View Deal
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
}